import { Button, Menu, Select, Tooltip } from '@mantine/core';
import {
  IconAnchor,
  IconBinaryTree2,
  IconFileExport,
  IconFlag3Filled,
  IconLock,
  IconPlus,
  IconSearch,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { ShipGlyph } from '../../VesselBadge.js';
import type { PersonnelVessel } from '../personnel/types.js';
import type { EquipmentOption } from './equipment-tree.js';
import classes from './Pms.module.css';
import type { VesselParticulars } from './types.js';

export interface VesselPmsCardProps {
  particulars: VesselParticulars | null;
  vessels: readonly PersonnelVessel[];
  scope: 'web' | 'ship';
  /** Üst karttaki arama: ağaçtaki her düğüm. */
  options: readonly EquipmentOption[];
  canAdd: boolean;
  /** Ofiste etkin gemi: ekleme yerine "Edited on board" (K1). */
  editedOnBoard: boolean;
  onVessel: (id: string) => void;
  onPick: (id: string) => void;
  onAdd: () => void;
  /** 768–1023 px: ağaç çekmecede; düğme yalnız o genişlikte görünür. */
  onOpenTree?: () => void;
  /**
   * "Export" (plan M7 Task 29): seçili ekipman ve alt kalemleri Excel ya da CSV'ye (Equipment List
   * raporu, klas ve kritik sütunlarıyla [NR496 4.4]). Verilmezse düğme pasif ve ipuçludur.
   */
  onExport?: (format: 'xlsx' | 'csv') => void;
  /** Dosya hazırlanırken düğme yükleniyor görünür. */
  exporting?: boolean;
}

/**
 * PMS üst kartı (`gemi-pms-ekipman.jpeg`): gemi görseli, ad ve IMO; Vessel Type, Built, Flag, Next
 * Port; arama, "+ Add Equipment" ve "Export". İçerik kartıdır; PageHeader'da ayrıca gemi kartı
 * çizilmez. Web'de birden çok gemi varsa gemi seçici adın yerindedir (`VesselCard` kalıbı). Export
 * (M7e) seçili düğümün alt ağacını Excel ya da CSV'ye aktarır; kaynak dışa aktarmayı bilmiyorsa
 * pasif ve ipuçludur. Gemi fotoğrafı yoktur: gemi kartının çizimi kullanılır (M6d).
 */
export function VesselPmsCard({
  particulars,
  vessels,
  scope,
  options,
  canAdd,
  editedOnBoard,
  onVessel,
  onPick,
  onAdd,
  onOpenTree,
  onExport,
  exporting = false,
}: VesselPmsCardProps) {
  const { t } = useTranslation();
  const k = (key: string, values: Record<string, unknown> = {}) =>
    t(`pms.equipment.card.${key}`, values);
  const dash = '—';
  const selector = scope === 'web' && vessels.length > 1;
  const name = particulars?.name ?? vessels[0]?.name ?? '';
  const imo = particulars?.imo ?? null;

  return (
    <section className={classes.vesselCard} aria-label={name || k('vessel')}>
      <div className={classes.vesselIdentity}>
        <span className={classes.vesselThumb} aria-hidden>
          <ShipGlyph size={64} />
        </span>
        <div className={classes.vesselName}>
          {selector ? (
            <Select
              aria-label={k('vessel')}
              variant="unstyled"
              className={classes.vesselSelect}
              data={vessels.map((v) => ({ value: v.id, label: v.name }))}
              value={particulars?.id ?? null}
              allowDeselect={false}
              comboboxProps={{ width: 260, position: 'bottom-start' }}
              onChange={(value) => {
                if (value) onVessel(value);
              }}
            />
          ) : (
            <h2 className={classes.vesselTitle} title={name}>
              {name}
            </h2>
          )}
          {imo ? <span className={classes.vesselImo}>{k('imo', { imo })}</span> : null}
        </div>
      </div>

      <dl className={classes.vesselFacts}>
        <div className={classes.vesselFact}>
          <dt>{k('vesselType')}</dt>
          <dd>{particulars?.vesselType ?? dash}</dd>
        </div>
        <div className={classes.vesselFact}>
          <dt>{k('built')}</dt>
          <dd>{particulars?.builtYear ?? dash}</dd>
        </div>
        <div className={classes.vesselFact} data-icon>
          <dt>{k('flag')}</dt>
          <dd>
            <IconFlag3Filled size={18} className={classes.flagIcon} aria-hidden />
            {particulars?.flag ?? dash}
          </dd>
        </div>
        <div className={classes.vesselFact} data-port>
          <dt>
            <IconAnchor size={26} stroke={1.6} className={classes.portIcon} aria-hidden />
            {k('nextPort')}
          </dt>
          <dd>{particulars?.nextPort ?? dash}</dd>
        </div>
      </dl>

      <div className={classes.vesselActions}>
        {onOpenTree ? (
          <Button
            variant="default"
            className={classes.treeButton}
            leftSection={<IconBinaryTree2 size={16} aria-hidden />}
            onClick={onOpenTree}
          >
            {t('pms.equipment.tree.open')}
          </Button>
        ) : null}
        <Select
          className={classes.vesselSearch}
          aria-label={k('searchLabel')}
          placeholder={k('search')}
          leftSection={<IconSearch size={15} aria-hidden />}
          data={options.map((o) => ({ value: o.value, label: o.label }))}
          searchable
          clearable={false}
          value={null}
          nothingFoundMessage={k('noResults')}
          renderOption={({ option }) => {
            const path = options.find((o) => o.value === option.value)?.path;
            return (
              <span className={classes.searchOption}>
                <span>{option.label}</span>
                {path ? <span className={classes.searchPath}>{path}</span> : null}
              </span>
            );
          }}
          onChange={(value) => {
            if (value) onPick(value);
          }}
        />
        {canAdd ? (
          <Button
            className={classes.addButton}
            leftSection={<IconPlus size={16} aria-hidden />}
            onClick={onAdd}
          >
            {k('add')}
          </Button>
        ) : editedOnBoard ? (
          <Tooltip label={t('pms.equipment.details.editedOnBoardHint')} multiline w={240}>
            <span className={classes.onBoard}>
              <IconLock size={15} aria-hidden />
              {t('pms.equipment.details.editedOnBoard')}
            </span>
          </Tooltip>
        ) : null}
        {onExport ? (
          <Menu position="bottom-end" withinPortal>
            <Menu.Target>
              <Button
                variant="default"
                loading={exporting}
                title={t('pmsReports.export.exportHint')}
                leftSection={<IconFileExport size={16} aria-hidden />}
              >
                {k('export')}
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item onClick={() => onExport('xlsx')}>{t('pmsReports.export.excel')}</Menu.Item>
              <Menu.Item onClick={() => onExport('csv')}>{t('pmsReports.export.csv')}</Menu.Item>
            </Menu.Dropdown>
          </Menu>
        ) : (
          <Tooltip label={k('exportHint')}>
            <span className={classes.disabledWrap}>
              <Button
                variant="default"
                disabled
                leftSection={<IconFileExport size={16} aria-hidden />}
              >
                {k('export')}
              </Button>
            </span>
          </Tooltip>
        )}
      </div>
    </section>
  );
}
